import { useState } from 'react';
import { Link } from 'react-router-dom';

import useTranslations from '@/i18n/useTranslations';
import GoBackButton from '@/shared-components/buttons/components/GoBackButton';
import { resolveLocalizedPublicPath } from '@/utils/localePublicUrl';

import '../../artist-help/profile/styles.css';

export default function ProfileCollectorHelpShippingAndReturnsPage() {
  const t = useTranslations('collectors_help.shipping_and_returns');
  const [openSections, setOpenSections] = useState({
    shippingOriginalsLimitedEditions: false,
    buyerProtection: false,
    checkingOrderStatus: false,
    galleryPickup: false,
    customsFees: false,
    returningArtworks: false,
  });

  function toggleSection(sectionKey) {
    setOpenSections((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey],
    }));
  }

  return (
    <section className="artist-help-category">
      <header className="artist-help-category__header">
        <GoBackButton alternativeRoute="/profile/collector-help" />
        <h1>{t('title')}</h1>
      </header>

      <div className="artist-help-category__body">
        <section className="artist-help-category__card artist-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger"
            onClick={() => toggleSection('shippingOriginalsLimitedEditions')}
            aria-expanded={openSections.shippingOriginalsLimitedEditions}
          >
            <h2>{t('sections.shipping_originals_limited_editions.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon ${openSections.shippingOriginalsLimitedEditions ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.shippingOriginalsLimitedEditions && (
            <div className="artist-help-category__accordion-content">
              <div className="artist-help-category__stack">
                <h3>{t('sections.shipping_originals_limited_editions.originals_title')}</h3>
                <p>{t('sections.shipping_originals_limited_editions.originals_p1')}</p>
                <p>{t('sections.shipping_originals_limited_editions.originals_p2')}</p>
                <ul>
                  <li>{t('sections.shipping_originals_limited_editions.originals_list_1')}</li>
                  <li>{t('sections.shipping_originals_limited_editions.originals_list_2')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.shipping_originals_limited_editions.limited_editions_title')}</h3>
                <p>{t('sections.shipping_originals_limited_editions.limited_editions_p1')}</p>
                <ul>
                  <li>{t('sections.shipping_originals_limited_editions.limited_editions_list_1')}</li>
                  <li>{t('sections.shipping_originals_limited_editions.limited_editions_list_2')}</li>
                </ul>
              </div>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger"
            onClick={() => toggleSection('buyerProtection')}
            aria-expanded={openSections.buyerProtection}
          >
            <h2>{t('sections.buyer_protection.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon ${openSections.buyerProtection ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.buyerProtection && (
            <div className="artist-help-category__accordion-content">
              <p>{t('sections.buyer_protection.p1')}</p>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.secure_transactions_title')}</h3>
                <ul>
                  <li>{t('sections.buyer_protection.secure_transactions_list_1')}</li>
                  <li>{t('sections.buyer_protection.secure_transactions_list_2')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.authenticity_title')}</h3>
                <ul>
                  <li>{t('sections.buyer_protection.authenticity_list_1')}</li>
                  <li>{t('sections.buyer_protection.authenticity_list_2')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.cancellation_title')}</h3>
                <ul>
                  <li>{t('sections.buyer_protection.cancellation_list_1')}</li>
                  <li>{t('sections.buyer_protection.cancellation_list_2')}</li>
                  <li>{t('sections.buyer_protection.cancellation_list_3')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.communication_title')}</h3>
                <p>{t('sections.buyer_protection.communication_p1')}</p>
                <ul>
                  <li>{t('sections.buyer_protection.communication_list_1')}</li>
                  <li>{t('sections.buyer_protection.communication_list_2')}</li>
                  <li>{t('sections.buyer_protection.communication_list_3')}</li>
                </ul>
                <p>{t('sections.buyer_protection.communication_p2')}</p>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.insurance_title')}</h3>
                <p>{t('sections.buyer_protection.insurance_p1')}</p>
                <p>{t('sections.buyer_protection.insurance_p2')}</p>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.problem_title')}</h3>
                <ul>
                  <li>{t('sections.buyer_protection.problem_list_1')}</li>
                  <li>{t('sections.buyer_protection.problem_list_2')}</li>
                  <li>{t('sections.buyer_protection.problem_list_3')}</li>
                </ul>
                <p>{t('sections.buyer_protection.problem_p1')}</p>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.buyer_protection.returns_title')}</h3>
                <p>{t('sections.buyer_protection.returns_p1')}</p>
                <ul>
                  <li>{t('sections.buyer_protection.returns_list_1')}</li>
                  <li>{t('sections.buyer_protection.returns_list_2')}</li>
                </ul>
                <p>{t('sections.buyer_protection.returns_p2')}</p>
                <p>{t('sections.buyer_protection.returns_p3')}</p>
                <ul>
                  <li>
                    <a href={resolveLocalizedPublicPath('/buyer-protection')} target="_blank" rel="noopener noreferrer">
                      {t('sections.buyer_protection.returns_buyer_protection_link_label')}
                    </a>
                  </li>
                  <li>
                    <Link to="/return-and-refund-policy" state={{ fromPath: '/profile/collector-help/shipping-and-returns' }}>
                      {t('sections.buyer_protection.returns_originals_link_label')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/limited-editions-return-and-refund-policy"
                      state={{ fromPath: '/profile/collector-help/shipping-and-returns' }}
                    >
                      {t('sections.buyer_protection.returns_le_link_label')}
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="artist-help-category__note">
                <h3>{t('sections.buyer_protection.privacy_note_title')}</h3>
                <p>{t('sections.buyer_protection.privacy_note_p1')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger"
            onClick={() => toggleSection('checkingOrderStatus')}
            aria-expanded={openSections.checkingOrderStatus}
          >
            <h2>{t('sections.checking_order_status.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon ${openSections.checkingOrderStatus ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.checkingOrderStatus && (
            <div className="artist-help-category__accordion-content">
              <p>{t('sections.checking_order_status.p1')}</p>
              <ul>
                <li>{t('sections.checking_order_status.list_1')}</li>
                <li>{t('sections.checking_order_status.list_2')}</li>
                <li>{t('sections.checking_order_status.list_3')}</li>
              </ul>
              <p>{t('sections.checking_order_status.p2')}</p>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger"
            onClick={() => toggleSection('galleryPickup')}
            aria-expanded={openSections.galleryPickup}
          >
            <h2>{t('sections.gallery_pickup.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon ${openSections.galleryPickup ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.galleryPickup && (
            <div className="artist-help-category__accordion-content">
              <p>{t('sections.gallery_pickup.p1')}</p>
              <p>{t('sections.gallery_pickup.p2')}</p>

              <div className="artist-help-category__note">
                <h3>{t('sections.gallery_pickup.important_title')}</h3>
                <p>{t('sections.gallery_pickup.important_p1')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger"
            onClick={() => toggleSection('customsFees')}
            aria-expanded={openSections.customsFees}
          >
            <h2>{t('sections.customs_fees.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon ${openSections.customsFees ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.customsFees && (
            <div className="artist-help-category__accordion-content">
              <p>{t('sections.customs_fees.p1')}</p>
              <ul>
                <li>{t('sections.customs_fees.list_1')}</li>
                <li>{t('sections.customs_fees.list_2')}</li>
              </ul>

              <div className="artist-help-category__stack">
                <h3>{t('sections.customs_fees.eu_note_title')}</h3>
                <p>{t('sections.customs_fees.eu_note_p1')}</p>
                <p>{t('sections.customs_fees.eu_note_p2')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger"
            onClick={() => toggleSection('returningArtworks')}
            aria-expanded={openSections.returningArtworks}
          >
            <h2>{t('sections.returning_artworks.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon ${openSections.returningArtworks ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.returningArtworks && (
            <div className="artist-help-category__accordion-content">
              <p>{t('sections.returning_artworks.p1')}</p>

              <div className="artist-help-category__stack">
                <h3>{t('sections.returning_artworks.first_24_title')}</h3>
                <p>{t('sections.returning_artworks.first_24_p1')}</p>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.returning_artworks.after_24_title')}</h3>
                <p>{t('sections.returning_artworks.after_24_p1')}</p>
                <ul>
                  <li>{t('sections.returning_artworks.after_24_list_1')}</li>
                  <li>
                    {t('sections.returning_artworks.after_24_list_2')}{' '}
                    <Link
                      to="/limited-editions-return-and-refund-policy"
                      state={{ fromPath: '/profile/collector-help/shipping-and-returns' }}
                    >
                      {t('sections.buyer_protection.returns_le_link_label')}
                    </Link>
                    .
                  </li>
                </ul>
              </div>

              <div className="artist-help-category__stack">
                <h3>{t('sections.returning_artworks.issue_title')}</h3>
                <p>{t('sections.returning_artworks.issue_p1')}</p>
                <ul>
                  <li>{t('sections.returning_artworks.issue_list_1')}</li>
                  <li>{t('sections.returning_artworks.issue_list_2')}</li>
                  <li>{t('sections.returning_artworks.issue_list_3')}</li>
                </ul>
              </div>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
